import { useState } from "react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, DataTable, GhostButton, PrimaryButton } from "@/components/admin/ui";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg, formatDateTime } from "@/lib/api";
import { Pill, PAYMENT_STATE } from "@/lib/status";

function RefundDialog({ purchase, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/purchases/${purchase.id}/refund`, { reason });
      toast.success("Payment refunded and assessment access revoked");
      onClose();
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!purchase} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-2xl sm:max-w-md" data-testid="refund-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Refund payment</DialogTitle><DialogDescription>Rs. {purchase?.amount_inr} for {purchase?.student_name}. Assessment access will be revoked.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Textarea required minLength={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for refund" data-testid="refund-reason-input" />
          <PrimaryButton type="submit" busy={busy} className="w-full bg-rose-700 hover:bg-rose-800" data-testid="refund-submit">Confirm refund</PrimaryButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Payments() {
  const [state, setState] = useState("all");
  const [refunding, setRefunding] = useState(null);
  const { data, loading, reload } = useFetch(`/admin/purchases${state !== "all" ? `?state=${state}` : ""}`);
  return (
    <div data-testid="payments-page">
      <PageHeader eyebrow="Revenue" title="Payments" description="Online (Razorpay, verified on the server) and offline payments recorded by admins." />
      <Select value={state} onValueChange={setState}>
        <SelectTrigger className="h-10 w-48 bg-white mb-4" data-testid="payments-state-filter"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white"><SelectItem value="all">All states</SelectItem>{Object.entries(PAYMENT_STATE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
      </Select>
      <DataTable testId="payments-table" loading={loading} rows={data} columns={[
        { key: "student_name", label: "Student", render: (r) => <span className="font-semibold">{r.student_name}</span> },
        { key: "parent_email", label: "Parent" },
        { key: "amount_inr", label: "Amount", render: (r) => <span className="font-mono">Rs. {r.amount_inr}</span> },
        { key: "method", label: "Method", render: (r) => (r.method === "offline_admin" ? `Offline - ${r.offline_reference}` : `Razorpay ${r.provider_payment_id || r.provider_order_id || ""}`) },
        { key: "state", label: "State", render: (r) => <Pill map={PAYMENT_STATE} value={r.state} /> },
        { key: "created_at", label: "Created", render: (r) => formatDateTime(r.created_at) },
        { key: "a", label: "", render: (r) => r.state === "PAYMENT_SUCCESS" && <GhostButton onClick={() => setRefunding(r)} data-testid={`refund-button-${r.id}`}>Refund</GhostButton> },
      ]} />
      <RefundDialog purchase={refunding} onClose={() => setRefunding(null)} onDone={reload} />
    </div>
  );
}
